import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { applyMention, type MentionCandidate } from '../mentions/applyMention';
import { findMentionQuery } from '../mentions/findMentionQuery';
import { reconcileMentions } from '../mentions/reconcileMentions';
import type { ChatMention } from '../types';

/** Enough to choose from without covering the conversation on a phone. */
const MAX_SUGGESTIONS = 6;

/** Word-prefix match: "@pop" finds "Ana Pop", "@an pop" finds it too. */
function matches(name: string, query: string): boolean {
  if (!query) return true;
  const needle = query.toLowerCase();
  const haystack = name.toLowerCase();
  if (haystack.startsWith(needle)) return true;
  return haystack.split(/\s+/).some((word) => word.startsWith(needle));
}

type Params = {
  value: string;
  mentions: ChatMention[];
  candidates: MentionCandidate[];
  onChange: (text: string, mentions: ChatMention[]) => void;
};

/**
 * The `@` picker's behaviour, kept out of the composer's markup.
 *
 * Two things here are load-bearing and easy to lose in a refactor:
 *
 * 1. Enter is intercepted ONLY when a suggestion is highlighted. The composer is a single
 *    text input inside a <form>, so Enter submits implicitly; preventDefault on the
 *    keydown is the only thing standing between "pick Ana" and "send `@an`". Everywhere
 *    else Enter must still send, because enterKeyHint="send" has promised the soft
 *    keyboard it will.
 * 2. activeIndex starts at -1, so on a phone -- no arrow keys -- Enter always sends and
 *    picking is a tap. Enter-to-pick is a desktop affordance that costs mobile nothing.
 */
export function useMentionAutocomplete({ value, mentions, candidates, onChange }: Params) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingCaretRef = useRef<number | null>(null);
  const [caret, setCaret] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const query = useMemo(() => (dismissed ? null : findMentionQuery(value, caret)), [value, caret, dismissed]);

  const suggestions = useMemo(() => {
    if (!query || candidates.length === 0) return [];
    return candidates.filter((c) => matches(c.name, query.query)).slice(0, MAX_SUGGESTIONS);
  }, [query, candidates]);

  const open = suggestions.length > 0;

  // Restore the caret after a pick. The input is controlled, so re-rendering with new
  // text drops the caret to the end -- and the insertion point is usually mid-string.
  // useLayoutEffect, not useEffect: this must land before paint or the caret visibly jumps.
  useLayoutEffect(() => {
    const position = pendingCaretRef.current;
    if (position == null) return;
    pendingCaretRef.current = null;
    const element = inputRef.current;
    if (!element) return;
    element.focus();
    element.setSelectionRange(position, position);
    setCaret(position);
  }, [value]);

  const handleTextChange = useCallback(
    (next: string, nextCaret: number) => {
      onChange(next, reconcileMentions(value, mentions, next));
      setCaret(nextCaret);
      setDismissed(false);
      setActiveIndex(-1);
    },
    [onChange, value, mentions],
  );

  const handleCaretChange = useCallback((position: number) => {
    setCaret(position);
  }, []);

  const select = useCallback(
    (candidate: MentionCandidate) => {
      if (!query) return;
      const result = applyMention(value, mentions, query.start, caret, candidate);
      pendingCaretRef.current = result.caret;
      onChange(result.text, result.mentions);
      setActiveIndex(-1);
      setDismissed(false);
    },
    [query, value, mentions, caret, onChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Android IMEs dispatch keyCode 229 mid-composition with an unreliable `key`.
      if (event.nativeEvent.isComposing || event.keyCode === 229) return;
      if (!open) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex((i) => (i + 1) % suggestions.length);
          return;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
          return;
        case 'Enter':
          if (activeIndex < 0) return; // let the form submit, as it always has
          event.preventDefault();
          select(suggestions[activeIndex]);
          return;
        case 'Tab':
          // Tab picks the first match even with nothing highlighted, so desktop has a
          // one-key path that does not have to touch Enter.
          event.preventDefault();
          select(suggestions[activeIndex < 0 ? 0 : activeIndex]);
          return;
        case 'Escape':
          // stopPropagation as well: the chat sits inside the Communication page's
          // chrome, and a bare Escape would close that out from under the user.
          event.preventDefault();
          event.stopPropagation();
          setDismissed(true);
          setActiveIndex(-1);
          return;
        default:
      }
    },
    [open, suggestions, activeIndex, select],
  );

  const reset = useCallback(() => {
    setCaret(0);
    setDismissed(false);
    setActiveIndex(-1);
  }, []);

  return {
    inputRef,
    suggestions,
    open,
    activeIndex,
    setActiveIndex,
    handleTextChange,
    handleCaretChange,
    handleKeyDown,
    select,
    reset,
  };
}
